import { useEffect } from 'preact/hooks';
import { signal } from '@preact/signals';
import { api } from '../../api/client';
import { InsightCard } from '../../components/intelligence/InsightCard';
import { FilterBar, createFilterState, filtersToParams } from '../../components/intelligence/FilterBar';
import { SkeletonCard } from '../../components/intelligence/SkeletonLoader';
import { Button } from '../../components/ui';

interface Insight {
  id: string;
  title: string;
  impact: 'high' | 'medium' | 'low';
  confidence: number;
  evidence: string;
  actions: string[];
  category: string;
  created_at: string;
}

const loading = signal(true);
const error = signal('');
const insights = signal<Insight[]>([]);
const filters = createFilterState();
const filterImpact = signal<string>('');

async function loadData() {
  loading.value = true;
  error.value = '';
  try {
    const params = filtersToParams(filters.value);
    const res = await api.get<{ insights: Insight[] }>(`/intelligence/insights?${params.toString()}`);
    insights.value = res.insights || [];
  } catch (err: any) {
    error.value = err.message || 'Erro ao carregar insights';
    insights.value = [];
  } finally {
    loading.value = false;
  }
}

export function InsightsPanel() {
  useEffect(() => { loadData(); }, []);

  const filtered = filterImpact.value
    ? insights.value.filter((i) => i.impact === filterImpact.value)
    : insights.value;

  return (
    <div>
 <h2 class="text-xl font-bold text-white mb-4">Insights</h2>
      <FilterBar filters={filters} onApply={loadData} />

      {/* Impact filter */}
      <div class="flex gap-2 mb-4">
        {['', 'high', 'medium', 'low'].map((v) => (
          <Button variant="ghost"
            key={v}
            class={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              filterImpact.value === v
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-gray-200'
            }`}
            onClick={() => { filterImpact.value = v; }}
          >
 {v === '' ? 'Todos' : v === 'high' ? 'Alto' : v === 'medium' ? 'Médio' : 'Baixo'}
          </Button>
        ))}
      </div>

      {error.value && (
        <div class="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg p-3 mb-4 text-sm">{error.value}</div>
      )}

      <div class="space-y-4">
        {loading.value ? (
          Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} lines={4} />)
        ) : filtered.length === 0 ? (
          <div class="text-gray-500 text-center py-12">Nenhum insight encontrado</div>
        ) : (
          filtered.map((ins) => (
            <InsightCard
              key={ins.id}
              title={ins.title}
              impact={ins.impact}
              confidence={ins.confidence}
              evidence={ins.evidence}
              actions={ins.actions}
              category={ins.category}
            />
          ))
        )}
      </div>
    </div>
  );
}
